import React, { useMemo, useState } from 'react';
import { Network, Search, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { ORG, buildOrgTree, initials } from '../../mock_data/org/orgData';
import { getColor } from '../../theme/colors';

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.1;
const DEFAULT_ZOOM = 1.0;

// Card matches Pulse_Base RoleCard.tsx: fully filled with department color,
// ~240px wide, dark border, two-row layout — title + dept label on top,
// avatar + person name underneath separated by a thin divider.
function PersonCard({ person, department, onClick, dimmed }) {
  const color = getColor(person.avatarColor || department?.colorName || 'darkblue');
  // Leadership = direct report to CEO (or the CEO themself).
  const isLeadership = !person.reportsTo || person.role === 'CEO';

  return (
    <button
      onClick={() => onClick?.(person)}
      className={`group w-60 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-150 text-left border border-black/10 ${
        dimmed ? 'opacity-20' : ''
      } ${color.bgSolid} text-white`}
    >
      <div className="p-3 space-y-2.5">
        {/* Title fills the row; dept name + leadership status on the next line. */}
        <div className="min-w-0">
          <div className="font-semibold text-sm leading-tight truncate">
            {person.role}
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wide opacity-80 truncate">
              {department?.name || 'No Dept'}
            </span>
            {isLeadership && (
              <span className="text-[10px] font-medium opacity-80">
                Leadership
              </span>
            )}
          </div>
        </div>

        {/* Holder row — divider matches the Pulse_Base look (rgb(0 0 0 / 15%)). */}
        <div className="pt-2 border-t border-black/15">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-white/95 text-slate-800 text-[11px] font-semibold flex items-center justify-center flex-shrink-0 ring-2 ring-white">
              {initials(person.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">
                {person.name}
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function TreeNode({ person, childrenMap, departmentsById, onPersonClick, filterText }) {
  const children = childrenMap.get(person.id) || [];
  const department = departmentsById[person.department];
  const matches = !filterText || matchesPerson(person, filterText, departmentsById);

  return (
    <div className="flex flex-col items-center">
      {/* Connector line up to parent (drawn by parent below) */}
      <PersonCard
        person={person}
        department={department}
        onClick={onPersonClick}
        dimmed={Boolean(filterText) && !matches}
      />

      {children.length > 0 && (
        <>
          {/* Vertical connector down from this card to the children's bus */}
          <div className="w-px h-6 bg-gray-300" />
          {/* Children container: relative so a single absolute horizontal
              bus line can span exactly between the centers of the leftmost
              and rightmost child cards (using padding offsets), then each
              child drops its own short vertical stub from that bus. */}
          <div className="relative flex items-start gap-10">
            {children.length > 1 && (
              <div
                className="absolute top-0 h-px bg-gray-300"
                style={{ left: 'calc(120px)', right: 'calc(120px)' }}
              />
            )}
            {children.map((child) => (
              <div key={child.id} className="flex flex-col items-center">
                {/* Stub from horizontal bus down into the child card */}
                <div className="w-px h-6 bg-gray-300" />
                <TreeNode
                  person={child}
                  childrenMap={childrenMap}
                  departmentsById={departmentsById}
                  onPersonClick={onPersonClick}
                  filterText={filterText}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function matchesPerson(person, filter, deptsById) {
  const q = filter.toLowerCase();
  return (
    person.name.toLowerCase().includes(q) ||
    person.role.toLowerCase().includes(q) ||
    (deptsById[person.department]?.name || '').toLowerCase().includes(q)
  );
}

function PersonDetail({ person, department, manager, reports, onClose }) {
  const color = getColor(person.avatarColor || department?.colorName || 'darkblue');
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-lg p-4 w-72">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-12 h-12 rounded-full ${color.bgSolid} text-white text-sm font-semibold flex items-center justify-center flex-shrink-0`}>
            {initials(person.name)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{person.name}</div>
            <div className="text-xs text-gray-500 truncate">{person.role}</div>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg leading-none" aria-label="Close">×</button>
      </div>

      <div className="space-y-2 text-xs">
        {department && (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Department</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${color.bg} ${color.text}`}>
              {department.name}
            </span>
          </div>
        )}
        {manager && (
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Reports to</span>
            <span className="text-gray-800 font-medium truncate ml-2">{manager.name}</span>
          </div>
        )}
        {reports.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
              Direct reports · {reports.length}
            </div>
            <ul className="space-y-1">
              {reports.map((r) => (
                <li key={r.id} className="text-gray-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  <span className="truncate">{r.name}</span>
                  <span className="text-gray-400 truncate">· {r.role}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrgChart() {
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const handleZoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const handleFit = () => setZoom(DEFAULT_ZOOM);

  const childrenMap = useMemo(() => buildOrgTree(ORG.people), []);
  const departmentsById = useMemo(
    () => Object.fromEntries(ORG.departments.map((d) => [d.id, d])),
    []
  );
  const peopleById = useMemo(
    () => Object.fromEntries(ORG.people.map((p) => [p.id, p])),
    []
  );

  const roots = childrenMap.get('__root__') || [];

  const selectedManager = selected?.reportsTo ? peopleById[selected.reportsTo] : null;
  const selectedReports = selected ? childrenMap.get(selected.id) || [] : [];

  return (
    <div className="page-wrapper">
      <div className="layout-container py-6 px-4">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Network className="w-5 h-5 text-darkblue-500" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900">Org Chart</h1>
              <p className="text-xs text-gray-500">{ORG.organization.name} · {ORG.people.length} people across {ORG.departments.length} departments</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Zoom controls */}
            <div className="inline-flex items-center bg-white border border-gray-200 rounded-md overflow-hidden">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                className="px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom out"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="px-2 text-[11px] font-mono text-gray-600 tabular-nums w-12 text-center border-x border-gray-200">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                className="px-2 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom in"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleFit}
                className="px-2 py-1.5 text-gray-600 hover:bg-gray-50 border-l border-gray-200"
                title="Fit to screen"
                aria-label="Fit to screen"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by name, role, department…"
                className="pl-8 pr-3 py-2 w-72 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-darkblue-400"
              />
            </div>
          </div>
        </div>

        {/* Department legend */}
        <div className="flex items-center gap-3 flex-wrap mb-6 text-[11px] text-gray-500">
          {ORG.departments.map((d) => {
            const c = getColor(d.colorName);
            return (
              <div key={d.id} className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${c.bgSolid}`} />
                {d.name}
              </div>
            );
          })}
        </div>

        {/* Tree — wrapped in a transform-scaled container driven by the
            zoom controls. transform-origin top-center keeps the root
            anchored as we scale up/down. */}
        <div className="overflow-auto pb-8 relative">
          <div
            className="flex justify-center min-w-fit transition-transform duration-150 ease-out"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
          >
            {roots.map((root) => (
              <TreeNode
                key={root.id}
                person={root}
                childrenMap={childrenMap}
                departmentsById={departmentsById}
                onPersonClick={setSelected}
                filterText={filter}
              />
            ))}
          </div>
        </div>

        {/* Detail popover (floating) */}
        {selected && (
          <div className="fixed bottom-6 right-6 z-50">
            <PersonDetail
              person={selected}
              department={departmentsById[selected.department]}
              manager={selectedManager}
              reports={selectedReports}
              onClose={() => setSelected(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
